import {
    Popover,
    PopoverPropsStrict,
    JustifyProps,
    Modal,
    ModalProps,
    DrawerProps,
    Drawer,
    UseFocusTrapProps,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { FC, ReactElement, ReactNode, useEffect } from 'react';
import { mobileObserver } from '../mode-observer';
import { PortalTakeover, PortalTakeoverProps } from './takeover';

interface PortalModalComponentProps {
    title: string;
    className?: string;
    mobileClassName?: string;
    desktopClassName?: string;
    contentClassName?: string;
    mobileContentClassName?: string;
    desktopContentClassName?: string;
    focusTrapOptions?: UseFocusTrapProps;
    footer?: ReactElement;
    footerAlign?: JustifyProps;
    mobile: boolean;
    thin?: boolean;
    open: boolean;
    trigger?: ReactElement;
    takeover?: Partial<
        Omit<
            PortalTakeoverProps,
            'title' | 'open' | 'onClose' | 'focusTrapOptions' | 'footerAlign' | 'footer'
        >
    >;
    children?: ReactNode;
    onClose?(): void;
}

export interface PortalPopoverProps extends Omit<PortalModalComponentProps, 'trigger'> {
    trigger: ReactElement;
    popover?: Omit<
        PopoverPropsStrict,
        'open' | 'focusTrapOptions' | 'footerAlign' | 'footer' | 'trigger'
    >;
}

export interface PortalModalProps extends Omit<PortalModalComponentProps, 'trigger'> {
    trigger?: ReactElement;
    modal?: Omit<
        ModalProps,
        'open' | 'onClose' | 'title' | 'focusTrapOptions' | 'footerAlign' | 'footer'
    >;
}

export interface PortalDrawerProps extends Omit<PortalModalComponentProps, 'trigger'> {
    drawer?: Omit<
        DrawerProps,
        'open' | 'onClose' | 'title' | 'focusTrapOptions' | 'footerAlign' | 'footer'
    >;
}

const TakeoverModal: FC<PortalModalComponentProps> = props => (
    <PortalTakeover
        title={props.title}
        className={classNames(props.className, props.mobileClassName)}
        open={props.open}
        onClose={props.onClose && !props.takeover?.actions ? props.onClose : undefined}
        onBack={
            props.onClose && props.takeover?.backLabel !== undefined ? props.onClose : undefined
        }
        contentWrapperClass={classNames(
            { 'p-0-i': props.thin },
            props.contentClassName,
            props.mobileContentClassName
        )}
        focusTrapOptions={props.focusTrapOptions}
        footerAlign={props.footerAlign}
        footer={props.footer}
        portal
        mobile
        {...(props.takeover ?? {})}
    >
        <PortalTakeover.Section className={classNames({ 'p-0-i': props.thin })}>
            {props.children}
        </PortalTakeover.Section>
    </PortalTakeover>
);

export const PortalPopover: FC<PortalPopoverProps> = props => {
    return props.open ? (
        !props.mobile ? (
            <Popover
                className={classNames(props.className, props.desktopClassName)}
                popoverContentClassName={classNames(
                    {
                        'm-0-i w-auto-i p-0-i': props.thin,
                    },
                    props.contentClassName,
                    props.desktopContentClassName
                )}
                width="auto"
                direction="br"
                open={props.open}
                focusTrapOptions={props.focusTrapOptions}
                footerAlign={props.footerAlign}
                footer={props.footer}
                trigger={props.trigger}
                onClickOutside={props.onClose}
                portal
                {...(props.popover ?? {})}
            >
                {props.children}
            </Popover>
        ) : (
            <TakeoverModal {...props} />
        )
    ) : (
        (props.trigger ?? null)
    );
};

export const PortalModal: FC<PortalModalProps> = props => {
    return props.open ? (
        props.mobile ? (
            <TakeoverModal {...props} />
        ) : (
            <Modal
                className={classNames(props.className, props.desktopClassName)}
                contentClassName={classNames(props.contentClassName, props.desktopContentClassName)}
                closable
                open={props.open}
                onClose={props.onClose}
                title={props.title}
                size={Modal.Sizes.M}
                focusTrapOptions={props.focusTrapOptions}
                footerAlign={props.footerAlign}
                footer={props.footer}
                {...(props.modal ?? {})}
            >
                {props.children}
            </Modal>
        )
    ) : null;
};

export const PortalDrawer: FC<PortalDrawerProps> = props => {
    useEffect(() => {
        if (!props.mobile && props.open) {
            document.body.classList.add('Takeover--is-open');
        }

        return () => {
            document.body.classList.remove('Takeover--is-open');
        };
    }, [props.mobile, props.open]);

    return props.open ? (
        props.mobile ? (
            <TakeoverModal {...props} />
        ) : (
            <Drawer
                className={classNames(props.className, props.desktopClassName)}
                contentClassName={classNames(props.contentClassName, props.desktopContentClassName)}
                closable
                open={props.open}
                onClose={props.onClose}
                header={props.title}
                focusTrapOptions={props.focusTrapOptions}
                footerAlign={props.footerAlign}
                footer={props.footer}
                backdrop
                {...(props.drawer ?? {})}
            >
                {props.children}
            </Drawer>
        )
    ) : null;
};

export const PortalPopoverObserver = mobileObserver(PortalPopover);
export const PortalModalObserver = mobileObserver(PortalModal);
export const PortalDrawerObserver = mobileObserver(PortalDrawer);
